// 商品內容頁改回 RGB 商品內容頁前的暫時外站入口
// TODO: 待 RGB 商品內容頁重新開放且確認 master、prdtesting 可曝光後，將此入口改回站內 Detail route。
export const LEGACY_RGB_PRODUCT_DETAIL_URL =
    'https://wish.gainhow.tw/ProductDetail/66420dd2-f1ca-11ec-a1ff-0242ac1b0002';

// 桌月曆圖庫
export const CALENDAR_GALLERY_URL =
    'https://www.gding.com.tw/calendar/2027/calendar-a.htm';

// 預設 SuperClassID
export const DEFAULT_SUPER_CLASS_ID = 'BusinessCard';

// ProductClass (原始 API 形狀 + 前端補上的圖片欄位)
export type ProductClass = {
    ProductClassID: string;
    ProductClassName: string;
    ProductCode: string;
    Type: number;
    Image?: string;
    ImageDetail?: string[];
    Description?: string;
    ProcessTime?: string;
    MinOrderQty?: number;
    Unit?: string;
    Price?: number;
    STotalPrice?: number;
    DTotalPrice?: number;
    OnlineEditURL?: string;
    // normalizer 依 Image / ImageDetail 補上的顯示用路徑
    ImgUrl?: string;
    ImgDetailUrl?: string[];
    [key: string]: unknown;
};

// normalizer 後的 ProductClass，保證有顯示用圖片欄位
export type NormalizedProductClass = ProductClass & {
    ImgUrl: string;
    ImgDetailUrl: string[];
};

// normalizer 後的 SubMainClass
export type NormalizedProductSubMainClass = ProductSubMainClass & {
    ProductClassList: NormalizedProductClass[];
};

// SubMainClass (含 ProductClassList 與價格表/尺寸規格圖)
export type ProductSubMainClass = {
    SubMainClassID: string;
    SubMainClassName: string;
    ProductCode: string;
    SpecImage?: string;
    DownloadURL?: string;
    VIPPricingImage?: string[];
    NonVIPPricingImage?: string[];
    ProductClassList?: ProductClass[];
    [key: string]: unknown;
};

// MainClass (左側 sidebar 樹狀來源)
export type ProductMainClass = {
    MainClassID: string;
    MainClassName: string;
    ProductCode: string;
    ProductSubMainClassList?: Array<{
        SubMainClassID: string;
        SubMainClassName: string;
        ProductCode: string;
    }>;
    [key: string]: unknown;
};

// gh-sidebar 需要的固定欄位
export type ClassificationSidebarItem = {
    value: string;
    label: string;
    code: string;
    children: Array<{
        value: string;
        label: string;
        code: string;
    }>;
};

// 整頁首屏資料，欄位對齊 ProductCategoryController SSR payload
export type ProductsClassificationInitialState = {
    currentMainClassID: string;
    currentSubMainClassID: string;
    superClassID: string;
    superClassName: string;
    isPostProcessing: boolean;
    authenticationType: string;
    isAuthorized: boolean;
    mainClassName: string;
    productCode: string;
    description: string;
    mainClassBannerImage: string;
    mainClassMobileBannerImage: string;
    productMainClassList: ProductMainClass[];
    productSubMains: ProductSubMainClass[];
};

type RawInitialState = {
    currentMainClassID?: string;
    currentSubMainClassID?: string;
    superClassID?: string;
    superClassName?: string;
    isPostProcessing?: boolean;
    authenticationType?: string;
    isAuthorized?: boolean;
    mainClassName?: string;
    productCode?: string;
    description?: string;
    mainClassBannerImage?: string;
    mainClassMobileBannerImage?: string;
    productMainClassList?: unknown;
    productSubMains?: unknown;
};

// 讀取 window 上由 inline script 注入的字串全域值 (CSR fallback 沿用 legacy 注入)
const readGlobalString = (key: string): string => {
    const value = (window as unknown as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : '';
};

// 讀取並正規化 SSR initial state，SSR 關閉或缺值時回退 inline 全域值與 stable defaults
export const resolveProductsClassificationInitialState =
    (): ProductsClassificationInitialState => {
        const rawState = window.__GH_SSR_INITIAL_STATE__;
        const state = (
            rawState && typeof rawState === 'object' ? rawState : {}
        ) as RawInitialState;

        return {
            currentMainClassID: state.currentMainClassID || '',
            currentSubMainClassID: state.currentSubMainClassID || '',
            superClassID: state.superClassID || '',
            superClassName: state.superClassName || '',
            isPostProcessing: Boolean(state.isPostProcessing),
            // SSR 有值時以 payload 為準，CSR fallback 讀 legacy inline 全域值
            authenticationType:
                state.authenticationType ??
                readGlobalString('userAuthenticationType'),
            isAuthorized:
                typeof state.isAuthorized === 'boolean'
                    ? state.isAuthorized
                    : readGlobalString('isAuthorized') === 'True',
            mainClassName: state.mainClassName || '',
            productCode: state.productCode || '',
            description: state.description || '',
            mainClassBannerImage: state.mainClassBannerImage || '',
            mainClassMobileBannerImage: state.mainClassMobileBannerImage || '',
            productMainClassList: Array.isArray(state.productMainClassList)
                ? (state.productMainClassList as ProductMainClass[])
                : [],
            productSubMains: Array.isArray(state.productSubMains)
                ? (state.productSubMains as ProductSubMainClass[])
                : [],
        };
    };

// 取得 ProductClassImage URL，缺圖回退 ComingSoon
export const getProductClassImageUrl = (
    imageName: string | undefined,
    folderName: 'Class' | 'Detail'
): string => {
    if (!imageName) return '/Images/ComingSoon.png';

    return `/Images/FindPrice/${folderName}/${imageName}`;
};

// 為 SubMainClass 的每個商品預先算出 ImgUrl / ImgDetailUrl，供樣板直接取用
export const normalizeProductSubMains = (
    rawSubMains: ProductSubMainClass[] | undefined
): NormalizedProductSubMainClass[] => {
    if (!Array.isArray(rawSubMains)) return [];

    return rawSubMains.map((subMainClass) => {
        const productClassList = Array.isArray(subMainClass.ProductClassList)
            ? subMainClass.ProductClassList.map((product) => {
                  const imgDetailUrl = Array.isArray(product.ImageDetail)
                      ? product.ImageDetail.map((item) =>
                            getProductClassImageUrl(item, 'Detail')
                        )
                      : [];

                  return {
                      ...product,
                      ImgUrl: getProductClassImageUrl(product.Image, 'Class'),
                      ImgDetailUrl: imgDetailUrl,
                  };
              })
            : [];

        return {
            ...subMainClass,
            ProductClassList: productClassList,
        };
    });
};

// 將 MainClass 資料轉成 gh-sidebar 固定欄位
export const buildClassificationSidebarItems = (
    productMainClassList: ProductMainClass[] | undefined
): ClassificationSidebarItem[] => {
    return (productMainClassList || []).map((mainClass) => {
        const subMainItems = Array.isArray(mainClass.ProductSubMainClassList)
            ? mainClass.ProductSubMainClassList
            : [];

        return {
            value: mainClass.MainClassID || '',
            label: mainClass.MainClassName || '',
            code: mainClass.ProductCode || '',
            children: subMainItems.map((subMainClass) => ({
                value: subMainClass.SubMainClassID || '',
                label: subMainClass.SubMainClassName || '',
                code: subMainClass.ProductCode || '',
            })),
        };
    });
};

// 千分位整數字串，無值回傳 --
export const formattedNumber = (number: number | undefined): string => {
    if (!number || number === 0) return '--';
    return number.toLocaleString('en-US');
};

// 無條件捨去小數並加千分位，無值回傳 --
export const formattedPrice = (price: number | undefined): string => {
    if (!price || price === 0) return '--';
    return Math.floor(price).toLocaleString('en-US');
};

// 組出線上查價網址，獨立版印製轉導獨立版估價
export const getPriceCalculator2Url = (
    mainClassID: string,
    subMainClassID: string,
    productClassID: string
): string => {
    const main = (mainClassID || '').trim();
    const sub = (subMainClassID || '').trim();
    const product = (productClassID || '').trim();

    if (product === 'OffsetGHPaper') {
        return '/ProductCategory/PrintInquiry';
    }

    return `/ProductCategory/PriceCalculator2?MainClassID=${main}&SubMainClassID=${sub}`;
};

// 組出分類頁路由網址：SuperClassID / MainClassID 走路徑，SubMainClassID 走 query
export const getProductsClassificationUrl = (
    superClassID: string,
    mainClassID: string,
    subMainClassID: string
): string => {
    const superClass = (superClassID || '').trim();
    const mainClass = (mainClassID || '').trim();
    const subMainClass = (subMainClassID || '').trim();

    if (!mainClass) {
        return `/ProductCategory/ProductsClassification/${encodeURIComponent(superClass)}`;
    }

    let url = `/ProductCategory/ProductsClassification/${encodeURIComponent(superClass)}/${encodeURIComponent(mainClass)}`;
    if (subMainClass) {
        url += `?SubMainClassID=${encodeURIComponent(subMainClass)}`;
    }
    return url;
};
